import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { completePersonalConnection } from '@/services/personal-connections/personal-connections';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';
import { ROUTES } from '@/constants/routes';

/**
 * Where a provider's OAuth screen returns (`?code=&state=`). The page
 * finishes the sign-in through the backend as the signed-in member, which
 * checks the single-use state was started by this same member; nothing
 * sensitive is kept here. In the sign-in popup it closes itself; in a full
 * tab it links back to Connectors.
 */
export const ConnectionOAuthReturnPage = () => {
  const { provider = '' } = useParams();
  const [params] = useSearchParams();
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid || '';
  const [status, setStatus] = useState<'working' | 'success' | 'error'>(
    'working',
  );
  const [detail, setDetail] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !orgId || !provider) return;
    started.current = true;
    const code = params.get('code') || '';
    const state = params.get('state') || '';
    if (!code || !state) {
      setStatus('error');
      setDetail(
        params.get('error_description') ||
          params.get('error') ||
          'The sign-in was canceled or did not return a code.',
      );
      return;
    }
    completePersonalConnection(orgId, provider, code, state)
      .then(() => {
        setStatus('success');
        window.setTimeout(() => window.close(), 1200);
      })
      .catch((error: unknown) => {
        setStatus('error');
        setDetail(
          error instanceof Error
            ? error.message
            : 'The sign-in could not be completed.',
        );
      });
  }, [orgId, provider, params]);

  const back = orgPath(
    `${ROUTES.ACCOUNT.CONNECTORS}?connected=${encodeURIComponent(provider)}`,
  );

  return (
    <div className='mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center'>
      {status === 'working' && (
        <>
          <Loader2 className='size-6 animate-spin' />
          <p>Finishing your sign-in…</p>
        </>
      )}
      {status === 'success' && (
        <>
          <CheckCircle2 className='size-6 text-green-600' />
          <p data-testid='oauth-return-success'>
            Connected. You can close this window.
          </p>
        </>
      )}
      {status === 'error' && (
        <>
          <XCircle className='size-6 text-destructive' />
          <p data-testid='oauth-return-error'>{detail}</p>
        </>
      )}
      {status !== 'working' && (
        <Button asChild variant='outline'>
          <Link to={back}>Back to Connectors</Link>
        </Button>
      )}
    </div>
  );
};
